import { apiRequest } from './client.js';

/**
 * An order needing no dealer, worked by its vendor alone: accept it, report it
 * completed with proofs, save, submit for the verification workflow.
 * Backend: OrderWorkCompletionController (/v1/billing/...).
 */
export interface OrderWorkProof {
  readonly proofId: string;
  readonly fileName: string;
  readonly mimeType: string;
  readonly sizeBytes: number;
  readonly uploadedAt: string;
  readonly proofTypeName: string | null;
  /** Where the photo was taken: from the photo itself, or typed in by someone allowed to. */
  readonly latitude: number | null;
  readonly longitude: number | null;
  readonly locationSource: 'PHOTO' | 'MANUAL' | null;
}

export interface OrderWorkCompletion {
  readonly completionId: string;
  readonly requestLineId: string;
  readonly status: 'DRAFT' | 'SUBMITTED' | 'APPROVED' | 'REJECTED';
  readonly remarks: string | null;
  readonly savedAt: string | null;
  readonly workflowInstanceId: string | null;
  readonly submittedAt: string | null;
  readonly decidedAt: string | null;
  readonly order: {
    readonly requestNo: string;
    readonly lineNumber: number;
    readonly typeName: string | null;
    readonly itemName: string | null;
    readonly quantity: string;
    readonly uomCode: string;
    readonly woNumber: string | null;
    readonly lineStatus: string;
  };
  readonly proofs: readonly OrderWorkProof[];
  /** The tenant's ORDER_WORK_PROOF_TYPE lookup values. */
  readonly proofTypes: ReadonlyArray<{ readonly id: string; readonly code: string; readonly name: string }>;
  /** What this sub-type's completion must carry (Branding activity types). */
  readonly evidence: { readonly requiresPhoto: boolean; readonly requiresGps: boolean };
  /** Whether this login may type in where a proof photo was taken. */
  readonly mayEnterLocation: boolean;
}

type Tok = string | null;

export const acceptOrderLine = (requestLineId: string, token: Tok) =>
  apiRequest<{ requestLineId: string; status: string }>(
    `/v1/billing/order-lines/${requestLineId}/accept`,
    { method: 'POST', token },
  );

/** "Job completed": opens the completion, or returns the one already open. */
export const startOrderCompletion = (requestLineId: string, token: Tok) =>
  apiRequest<OrderWorkCompletion>(
    `/v1/billing/order-lines/${requestLineId}/completion`,
    { method: 'POST', token },
  );

export const getOrderCompletion = (completionId: string, token: Tok) =>
  apiRequest<OrderWorkCompletion>(`/v1/billing/order-completions/${completionId}`, { token });

export function attachOrderProof(
  completionId: string,
  proofTypeValueId: string,
  file: File,
  token: Tok,
) {
  const body = new FormData();
  body.append('proofTypeValueId', proofTypeValueId);
  body.append('file', file);
  return apiRequest<OrderWorkProof>(
    `/v1/billing/order-completions/${completionId}/proofs`,
    { method: 'POST', token, body },
  );
}

export const removeOrderProof = (completionId: string, proofId: string, token: Tok) =>
  apiRequest<{ proofId: string }>(
    `/v1/billing/order-completions/${completionId}/proofs/${proofId}`,
    { method: 'DELETE', token },
  );

/** Where a proof photo was taken, typed in when the photo carries no position. */
export const setOrderProofLocation = (
  completionId: string,
  proofId: string,
  latitude: number,
  longitude: number,
  token: Tok,
) =>
  apiRequest<OrderWorkCompletion>(
    `/v1/billing/order-completions/${completionId}/proofs/${proofId}/location`,
    { method: 'PUT', token, body: { latitude, longitude } },
  );

export const saveOrderCompletion = (completionId: string, remarks: string, token: Tok) =>
  apiRequest<OrderWorkCompletion>(`/v1/billing/order-completions/${completionId}`, {
    method: 'PUT',
    token,
    body: { remarks },
  });

export const submitOrderCompletion = (completionId: string, token: Tok) =>
  apiRequest<OrderWorkCompletion>(
    `/v1/billing/order-completions/${completionId}/submit`,
    { method: 'POST', token },
  );

/** The path a proof's bytes are fetched from with the reader's login. */
export const orderProofContentPath = (completionId: string, proofId: string) =>
  `/v1/billing/order-completions/${completionId}/proofs/${proofId}/content`;
